import React, { useState, useEffect } from 'react';
import DashboardLayout from '../layout/DashboardLayout';
import '../../styles/PatientManagement.css';

const PatientManagement = () => {
  // eslint-disable-next-line no-unused-vars
  const [patients, setPatients] = useState([]);
  const [newPatient, setNewPatient] = useState({
    firstName: '',
    lastName: '',
    age: '',
    gender: '',
    roomNumber: '',
    medicalHistory: '',
    currentMedications: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });
  const [document, setDocument] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await fetch('https://clinic123.onrender.com/api/patients', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setPatients(data);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.entries(newPatient).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          Object.entries(value).forEach(([subKey, subValue]) => {
            formData.append(`emergencyContact.${subKey}`, subValue);
          });
        } else {
          formData.append(key, value);
        }
      });
      if (document) {
        formData.append('document', document);
      }
      const response = await fetch('https://clinic123.onrender.com/api/patients', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      if (response.ok) {
        setNewPatient({
          firstName: '',
          lastName: '',
          age: '',
          gender: '',
          roomNumber: '',
          medicalHistory: '',
          currentMedications: '',
          emergencyContact: {
            name: '',
            relationship: '',
            phone: ''
          }
        });
        setDocument(null);
        setSuccessMsg('Patient created successfully!');
        setTimeout(() => setSuccessMsg(''), 3000);
        fetchPatients();
      }
    } catch (error) {
      console.error('Error adding patient:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`https://clinic123.onrender.com/api/patients?search=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching patients:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('emergencyContact.')) {
      const field = name.split('.')[1];
      setNewPatient((prev) => ({
        ...prev,
        emergencyContact: {
          ...prev.emergencyContact,
          [field]: value
        }
      }));
    } else {
      setNewPatient((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDelete = async (patientId) => {
    if (!window.confirm('Are you sure you want to delete this patient?')) return;
    try {
      const response = await fetch(`https://clinic123.onrender.com/api/patients/${patientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        fetchPatients();
      }
    } catch (error) {
      console.error('Error deleting patient:', error);
    }
  };

  // Download patient document
  const handleDownloadDocument = async (patientId, fullName) => {
    try {
      const response = await fetch(`https://clinic123.onrender.com/api/patients/${patientId}/document`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to download document');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fullName || 'patient'}-document`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('Could not download document.');
      console.error(error);
    }
  };

  const userRole = localStorage.getItem('userRole');
  const userDepartment = localStorage.getItem('department');
  const canAddPatient =
    userRole === 'admin' ||
    userDepartment === 'Medical Department' ||
    userDepartment === 'Customer Service Department';
  // eslint-disable-next-line no-unused-vars
  const canDeletePatient = canAddPatient; // Only admins, Medical, Customer Service can delete

  return (
    <DashboardLayout pageTitle="Patient Management">
      <div className="patient-management-container">
        <div className="card patient-card">
          <h3>Search for Patient</h3>
          <form onSubmit={handleSearch} className="search-patient-form">
            <input
              type="text"
              placeholder="Search by name, room, or other info..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">Search</button>
          </form>
          <ul className="patient-list">
            {searchResults.map((patient) => (
              <li key={patient._id} className="patient-list-item">
                <span>
                  {patient.firstName} {patient.lastName} (Age: {patient.age}, Gender: {patient.gender}, Room: {patient.roomNumber})<br />
                  <strong>Admitted:</strong> {patient.createdAt ? new Date(patient.createdAt).toLocaleDateString() : 'N/A'}<br />
                  <strong>Discharged:</strong> {patient.dischargeDate ? new Date(patient.dischargeDate).toLocaleDateString() : 'Still Admitted'}
                </span>
                {patient.hasDocument && (
                  <button onClick={() => handleDownloadDocument(patient._id, `${patient.firstName}_${patient.lastName}`)} className="download-button" style={{marginLeft: '1rem'}}>Download Document</button>
                )}
              </li>
            ))}
          </ul>
        </div>
        {canAddPatient && (
          <div className="card patient-card">
            <h3>Add New Patient</h3>
            {successMsg && <div className="success-message">{successMsg}</div>}
            <form onSubmit={handleSubmit} className="add-patient-form modern-patient-form" encType="multipart/form-data">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input type="text" id="firstName" name="firstName" placeholder="First Name" value={newPatient.firstName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input type="text" id="lastName" name="lastName" placeholder="Last Name" value={newPatient.lastName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="age">Age</label>
                <input type="number" id="age" name="age" placeholder="Age" value={newPatient.age} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="gender">Gender</label>
                <input type="text" id="gender" name="gender" placeholder="Gender" value={newPatient.gender} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="roomNumber">Room Number</label>
                <input type="text" id="roomNumber" name="roomNumber" placeholder="Room Number" value={newPatient.roomNumber} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="medicalHistory">Medical History</label>
                <input type="text" id="medicalHistory" name="medicalHistory" placeholder="Medical History" value={newPatient.medicalHistory} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="currentMedications">Current Medications</label>
                <input type="text" id="currentMedications" name="currentMedications" placeholder="Current Medications" value={newPatient.currentMedications} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Emergency Contact Name</label>
                <input type="text" name="emergencyContact.name" placeholder="Contact Name" value={newPatient.emergencyContact.name} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Emergency Contact Relationship</label>
                <input type="text" name="emergencyContact.relationship" placeholder="Relationship" value={newPatient.emergencyContact.relationship} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Emergency Contact Phone</label>
                <input type="text" name="emergencyContact.phone" placeholder="Phone Number" value={newPatient.emergencyContact.phone} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="document">Attach Document (optional)</label>
                <input type="file" id="document" name="document" onChange={e => setDocument(e.target.files[0])} />
              </div>
              <button type="submit" className="add-button modern-add-button">Add Patient</button>
            </form>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
export default PatientManagement;
